import { useCallback, useEffect, useState } from "react";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { Panel, Btn } from "@/components/admin/AdminShell";
import { api } from "@/lib/api";

const OPTIONS = [
  {
    id: "meraotp",
    title: "MeraOTP",
    desc: "Real SMS via MeraOTP. The provider generates and verifies the code.",
  },
  {
    id: "dev",
    title: "Dev OTP",
    desc: "No SMS is sent. Every mobile OTP is 123456 and is shown in the API response.",
    danger: true,
  },
  {
    id: "auto",
    title: "Existing gateway (default)",
    desc: "Previous behaviour: PRP / AuthKey gateway, or env-based dev mode.",
  },
];

const EFFECTIVE_LABEL = { meraotp: "MeraOTP", dev: "Dev OTP", gateway: "Existing gateway" };

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

export default function OtpProviderPanel() {
  const [state, setState] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [msg, setMsg] = useState(null); // {ok, text}

  const load = useCallback(async (refresh = false) => {
    setLoadErr("");
    try {
      const { data } = await api.get("/api/admin/otp-provider", { params: refresh ? { refresh: true } : {} });
      setState(data);
    } catch (e) {
      setLoadErr(errText(e, "Could not load OTP provider settings."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function refresh() {
    setRefreshing(true);
    await load(true);
    setRefreshing(false);
  }

  async function choose(opt) {
    if (!state || saving || state.mode === opt.id) return;
    let confirmDev = false;
    if (opt.id === "dev") {
      const ok = window.confirm(
        "Switch to DEV OTP?\n\nNo SMS will be sent and every mobile OTP becomes 123456, " +
          "returned in the API response. Anyone who knows a phone number can pass mobile verification. " +
          "Never leave this on in production."
      );
      if (!ok) return;
      confirmDev = true;
    }
    setSaving(true);
    setMsg(null);
    try {
      await api.put("/api/admin/otp-provider", { mode: opt.id, confirm_dev: confirmDev });
      setMsg({ ok: true, text: `Mobile OTP provider set to ${opt.title}.` });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: errText(e, "Failed to change provider.") });
    } finally {
      setSaving(false);
    }
  }

  if (loadErr) {
    return <Panel className="p-5 max-w-[640px] text-[13px] text-red-400">{loadErr}</Panel>;
  }
  if (!state) {
    return <Panel className="p-5 max-w-[640px] text-[13px] text-muted-foreground">Loading…</Panel>;
  }

  const mera = state.meraotp || {};
  const plan = mera.plan;
  const effective = state.effective;
  const lowQuota = plan && plan.sms_remaining != null && plan.sms_remaining <= 3;

  return (
    <div className="space-y-4 max-w-[640px]" data-testid="otp-provider-settings">
      <Panel className="p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-[14px] font-semibold">Mobile OTP provider</h3>
          <span
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
              effective === "dev" ? "border-red-500 text-red-400" : "border-line text-muted-foreground"
            }`}
            data-testid="otp-effective-mode"
          >
            Active: {EFFECTIVE_LABEL[effective] || effective}
          </span>
        </div>
        <p className="text-[12px] text-muted-foreground mb-4">
          Applies to mobile OTPs only (login, signup, verification, account deletion). Email OTPs are unaffected.
          Changes take effect within a few seconds.
        </p>

        {effective === "dev" && (
          <div className="flex gap-2 p-3 mb-4 rounded-md border border-red-500/60 bg-red-500/10 text-[12px] text-red-300">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>Dev OTP is active: any mobile number can be verified with 123456. Switch back before going live.</span>
          </div>
        )}

        <div className="space-y-2">
          {OPTIONS.map((opt) => {
            const selected = state.mode === opt.id;
            const blockedReason =
              opt.id === "meraotp" && !mera.configured
                ? `Set ${(mera.missing || ["MERAOTP_API_TOKEN"]).join(", ")} on the server first.`
                : opt.id === "dev" && !state.dev_switch_allowed
                ? "Disabled by the server (OTP_ALLOW_DEV_SWITCH=false)."
                : "";
            return (
              <button
                key={opt.id}
                type="button"
                disabled={saving || !!blockedReason}
                onClick={() => choose(opt)}
                data-testid={`otp-provider-${opt.id}`}
                className={`w-full text-left p-3 rounded-md border transition ${
                  selected ? "border-primary bg-primary/10" : "border-line bg-background hover:border-primary/60"
                } ${blockedReason ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`w-3.5 h-3.5 rounded-full border ${
                      selected ? "border-primary bg-primary" : "border-line"
                    }`}
                  />
                  <span className={`text-[13px] font-semibold ${opt.danger ? "text-red-400" : ""}`}>{opt.title}</span>
                </div>
                <div className="text-[12px] text-muted-foreground mt-1 ml-[22px]">{opt.desc}</div>
                {blockedReason && <div className="text-[11px] text-yellow-400 mt-1 ml-[22px]">{blockedReason}</div>}
              </button>
            );
          })}
        </div>

        {msg && (
          <div className={`mt-3 text-[12px] ${msg.ok ? "text-green-400" : "text-red-400"}`} data-testid="otp-provider-msg">
            {msg.text}
          </div>
        )}
        {state.mode === "auto" && state.env_dev_return_otp && (
          <div className="mt-3 text-[11px] text-yellow-400">
            Server env DEV_RETURN_OTP is on, so "Existing gateway" currently behaves as Dev OTP.
          </div>
        )}
      </Panel>

      <Panel className="p-5" data-testid="meraotp-plan">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[14px] font-semibold">MeraOTP account</h3>
          <Btn onClick={refresh} disabled={refreshing || !mera.configured} data-testid="meraotp-refresh">
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refresh
          </Btn>
        </div>

        {!mera.configured ? (
          <div className="text-[12px] text-yellow-400">
            API token not configured. Set <code>MERAOTP_API_TOKEN</code> in the backend environment and restart the API.
          </div>
        ) : mera.plan_error ? (
          <div className="text-[12px] text-red-400">Could not reach MeraOTP: {mera.plan_error}</div>
        ) : !plan ? (
          <div className="text-[12px] text-red-400">No active plan. SMS sending will fail until a plan is purchased.</div>
        ) : (
          <>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              <dt className="text-muted-foreground">Plan</dt>
              <dd className="font-semibold">{plan.name}{plan.is_free ? " (free trial)" : ""}</dd>
              <dt className="text-muted-foreground">SMS used</dt>
              <dd>{plan.sms_count} / {plan.sms_limit} ({plan.sms_remaining} left)</dd>
              <dt className="text-muted-foreground">Days left</dt>
              <dd>{plan.days_left}</dd>
              <dt className="text-muted-foreground">Expires</dt>
              <dd>{plan.expires_at}</dd>
              <dt className="text-muted-foreground">OTP length</dt>
              <dd>{mera.otp_length} digits</dd>
            </dl>
            {plan.is_free && (
              <div className="mt-3 text-[12px] text-yellow-400">
                Free trial: only {plan.sms_limit} SMS in total, then OTP sign-in via MeraOTP stops working. Upgrade before launch.
              </div>
            )}
            {lowQuota && (
              <div className="mt-2 text-[12px] text-red-400">Low quota: {plan.sms_remaining} SMS remaining.</div>
            )}
          </>
        )}
      </Panel>
    </div>
  );
}
